// A person's AI budget (MI-354): how much of today's and this month's they have
// used, and when each starts again. The API sends a percentage and an instant;
// the words are the reader's, and the time is on their clock.
import { dayShort, formatTime12h, monthShort } from './intlFormatters';

// What a refusal over a limit says (a 429: a call limit, or the AI budget), as
// the API client already worded it in the reader's language; null for any
// other failure, which keeps its screen's own message.
export const limitRefusal = (error) =>
  (error?.response?.status === 429 && error.response.data?.detail) || null;

// The `details.type` of the 429 a paid call gets once the budget is reached,
// told apart from a call limit's.
export const AI_BUDGET_REACHED = 'ai-budget-reached';

// Whether a failure is the AI budget's refusal, not a call limit's or another
// error: a box with another way to do the same thing then says so.
export const isAiBudgetRefusal = (error) =>
  error?.response?.status === 429 && error.response.data?.details?.type === AI_BUDGET_REACHED;

// A budget's bar turns amber from here on, before the AI pauses at 100%; from
// here on too, a small ring beside Settings says the person is getting close.
export const WARN_AT = 80;

// Fired on the window after a paid AI call succeeds, so whatever shows the
// usage reads it again.
export const AI_USED_EVENT = 'mikapital:ai-used';

// The calls that spend the AI budget: the chat, the two AI boxes, a statement
// upload or its retry, and reading a statement again.
const PAID_AI_CALL = /^\/(chat\/messages|movements\/(filters\/)?parse|statements\/processes(\/[^/?]+\/retry)?|repairs\/statements\/[^/?]+\/check)(\?|$)/;

// Whether a request spends the AI budget, from its axios config.
export const isPaidAiCall = (config) =>
  config?.method === 'post' && PAID_AI_CALL.test(config.url || '');

// The window to warn about, the fuller of the two, once it reaches WARN_AT:
// `{ window, percent, reached }`, or null while both are below it.
export const aiUsageMark = (usage) => {
  const top = ['day', 'month']
    .filter((window) => usage?.[window])
    .map((window) => ({ window, percent: usage[window].percent }))
    .sort((a, b) => b.percent - a.percent)[0];
  if (!top || top.percent < WARN_AT) return null;
  return { ...top, reached: top.percent >= 100 };
};

// "Tue, Dec 1": the day a month's budget starts again.
const resetDay = (date) => `${dayShort(date.getDay())}, ${monthShort(date.getMonth())} ${date.getDate()}`;

// What a budget's reset reads as: a time for today's, a day for this month's.
// `t` is the settings translator.
export const resetsText = (t, window, resetsAt) => {
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return '';
  return window === 'day'
    ? t('plan.resetsAt', { time: formatTime12h(date) })
    : t('plan.resetsOn', { date: resetDay(date) });
};

// The refusal's message, in the reader's language. `t` is any translator that
// reaches the common namespace.
export const aiBudgetMessage = (t, { window, resets_at: resetsAt } = {}) => {
  const date = new Date(resetsAt);
  const valid = !Number.isNaN(date.getTime());
  return window === 'month'
    ? t('common:aiBudget.reachedMonth', { date: valid ? resetDay(date) : '' })
    : t('common:aiBudget.reachedDay', { time: valid ? formatTime12h(date) : '' });
};
